import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import API from '@/config/api';

import {
	conversationEvaluationsResponse,
	evaluationsListResponse,
	type EvaluationsParams,
	type UseConversationEvaluations,
	type UseEvaluations,
	type UseRunEvaluations,
} from './Evaluations.schema';

const EVALUATIONS_QUERY_KEY = ['evaluations'];

export const evaluationsKeys = {
	all: EVALUATIONS_QUERY_KEY,
	conversation: (threadId: string) => [...EVALUATIONS_QUERY_KEY, 'conversation', threadId] as const,
	list: (filters: EvaluationsParams) => [...EVALUATIONS_QUERY_KEY, 'list', filters] as const,
};

export const useConversationEvaluations = (threadId: string, opts: UseConversationEvaluations['opts'] = {}) =>
	useQuery<UseConversationEvaluations['response']>({
		...opts,
		queryKey: evaluationsKeys.conversation(threadId),
		queryFn: () =>
			API.get(`/conversations/${threadId}/evaluations`).then((res) => conversationEvaluationsResponse.parse(res.data)),
		enabled: !!threadId,
		staleTime: 15_000,
	});

export const useRunEvaluations = (opts: UseRunEvaluations['opts'] = {}) => {
	const qc = useQueryClient();

	return useMutation({
		...opts,
		mutationFn: ({ threadId }) =>
			API.post(`/conversations/${threadId}/evaluations/run`).then((res) =>
				conversationEvaluationsResponse.parse(res.data),
			),
		onSuccess: (data, vars, ctx) => {
			qc.setQueryData(evaluationsKeys.conversation(vars.threadId), data);
			qc.invalidateQueries({ queryKey: EVALUATIONS_QUERY_KEY });
			opts.onSuccess?.(data, vars, ctx);
		},
	});
};

export const useEvaluations = (params: EvaluationsParams = {}, opts: UseEvaluations['opts'] = {}) => {
	const { page = 1, pageSize = 20, ...filters } = params;

	return useQuery<UseEvaluations['response']>({
		...opts,
		queryKey: evaluationsKeys.list({ page, pageSize, ...filters }),
		queryFn: () =>
			API.get('/evaluations', { params: { page, pageSize, ...filters } }).then((res) =>
				evaluationsListResponse.parse(res.data),
			),
		staleTime: 15_000,
	});
};
